import { EditableComponent, EditableComponentProps } from '@adobe/aem-react-editable-components';
import React, { FC, useMemo, useState, useCallback, useEffect, useRef, startTransition } from 'react';
import { useDCAPageModelStore } from '@marriott/mi-store-utils';
import clsx from 'clsx';
import { renditions } from '@marriott/mi-ui-library';
import {
  DcaNode,
  DynamicTabbedLayoutCompProps,
  DynamicTabbedLayoutProps,
  SlotContent,
  IPageModel,
  IReturnTypeOFGetMboxParam,
  CfNode,
} from './DynamicTabbedLayout.types';
import { TabNavigation } from '../../molecules/Tabs/TabNavigation';
import { TabBanner } from '../../molecules/Tabs/TabBanner';
import { StyledDynamicTabbedLayout, StyledTabPanelGrid, StyledTabPanelItem } from './DynamicTabbedLayout.styles';
import {
  buildPatternSlots,
  overlaySlotsWithDcaRes,
  mergeCfAndDcaToSlots,
  normalizeCfItems,
  isDcaTargetPayloadFailed,
  mergeAlloyMboxResponses,
  resolveMboxResponses,
} from '../../utils/dynamicContentUtils';
import { dcaMockData } from './dcaMockContent.mock';
import { getDcaNonCobrandImpressionEntry, resolveSlotComponent } from '../../utils/resolveSlotComponent';
import { getMboxParamters, fetchDCADynamicModelForAlloy, useSession } from '@marriott/mi-headless-utils';
import { fetchCFData } from '../../utils/fetchCFData';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useGetUxlForDTL } from 'mi-homepage-graphql';
import { buildTabTrackingPrefix, resolveTabClickLocation } from '../../utils/polarisTrackingUtils';

const isMockCFDataEnabled = () => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const nextPublicMockCfData = process.env.NEXT_PUBLIC_USE_MOCK_CFDATA;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const mockCfData = process.env.USE_MOCK_CFDATA;
  return nextPublicMockCfData === 'true' || mockCfData === 'true';
};

const isMockDCADataEnabled = () => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const nextPublicMockDcaData = process.env.NEXT_PUBLIC_USE_MOCK_DCADATA;
  return nextPublicMockDcaData === 'true';
};

/** Prevents infinite passive-effect loops when overlay yields identical slots (new array identity each run). */
function isSameTabSlotArray(prev: SlotContent[] | undefined, next: SlotContent[]): boolean {
  if (prev === next) return true;
  if (!prev || prev.length !== next.length) return false;
  try {
    return JSON.stringify(prev) === JSON.stringify(next);
  } catch {
    return false;
  }
}

const TabContainerConfig = {
  emptyLabel: 'Dynamic Tabbed Layout',
  isEmpty: () => true,
  resourceType: `mi-aem-homepage-spa/components/content/dynamictabbedlayout`,
};

export const DynamicTabbedLayout = (props: EditableComponentProps & DynamicTabbedLayoutProps) => (
  <EditableComponent config={{ ...TabContainerConfig }} {...props}>
    <DynamicTabbedLayoutComp {...props.model} isAuthorMode={props.isAuthorMode} />
  </EditableComponent>
);

const DynamicTabbedLayoutComp: FC<DynamicTabbedLayoutCompProps> = (model: DynamicTabbedLayoutCompProps) => {
  const {
    styleclass,
    targetComponentName,
    trackingProperties,
    tabDetails = [],
    pageTitle,
    requestedTab,
    requestedTabData,
    isAuthorMode = false,
    isNextGenSearchForm = false,
  } = model;

  const getUxlForDTL = useGetUxlForDTL();
  const theme = styleclass?.includes('inverse') ? 'dark' : 'light';
  const [dcaResponseByMbox, setDCAResponseByMbox] = useState<Record<string, unknown>>({});
  const pageModel = useDCAPageModelStore(state => state.pageModel) as Record<string, Record<string, unknown>[]>;
  const { datalayer, mvpOffers } = useSession();

  // build tab list (tabTitle is user-facing and used for hash / API)
  const tabItemList = useMemo(() => {
    const tabClickLocation = resolveTabClickLocation(trackingProperties?.trackingContentPosition);

    return tabDetails.map((tab, index) => ({
      tabValue: tab.nodeName || `tab-${index}`,
      tabTitle: tab.tabName,
      tabIcon: tab.tabIcon?.split(':')[1] ?? '',
      tabTrackingDescription: tab.tabTrackingDescription,
      dynamicMedia: tab.dynamicMedia,
      customTabClickTrackValue: `${tabClickLocation}|${buildTabTrackingPrefix(index + 1, tab.tabName)}|internal`,
      tabDCAtiles: tab.dcaTilesList ?? [],
      tabPatternSlots: buildPatternSlots(tab.bannerFirst ?? false, tab.squareFirst ?? true, tab.dcaTilesList ?? []),
      tabDecisionScopes:
        (tab.dcaTilesList ?? []).map((dt: DcaNode) => dt.mboxParameter).filter((s): s is string => Boolean(s)) ?? [],
    }));
  }, [tabDetails, trackingProperties?.trackingContentPosition]);

  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string>(requestedTab ?? tabItemList[0]?.tabTitle);
  // tabSlots is a record of tab titles and their corresponding slots content
  const [tabSlots, setTabSlots] = useState<Record<string, SlotContent[]>>(() => {
    if (requestedTab && Array.isArray(requestedTabData)) {
      return { [requestedTab]: requestedTabData };
    }
    return {};
  });
  // tabSlotsRef is a ref to the tabSlots state
  // it is used to prevent re-rendering of the component when the tabSlots state changes
  const tabSlotsRef = useRef<Record<string, SlotContent[]>>(
    requestedTab && Array.isArray(requestedTabData) ? { [requestedTab]: requestedTabData } : {}
  );
  useEffect(() => {
    tabSlotsRef.current = tabSlots;
  }, [tabSlots]);

  // Derived: true when any mbox on the page carries a convoAI experience.
  // We scan the full pageModel['mboxData'] (not just the tab's dcaTilesList) because the
  // convoAI mbox (target-convoAI-mbox) is injected via a query-param override and is not
  // declared in any tab's dcaTilesList — so it never lands in dcaResponseByMbox.
  const isConvoAIActive = useMemo(() => {
    const mboxItems = (pageModel?.['mboxData'] as Record<string, unknown>[] | undefined) ?? [];
    return mboxItems.some(item => {
      const payload = (item as Record<string, unknown>)?.['uxlResponse'] ?? item;
      const meta =
        (payload as Record<string, unknown>)?.['metadata'] ?? (payload as Record<string, unknown>)?.['metaData'];
      return (meta as Record<string, unknown>)?.['experience'] === 'convoAI';
    });
  }, [pageModel]);

  // Initial load: seed DCA responses from the global store (populated by useDCADynamicModel).
  // getDcaData is not called on initial load.
  useEffect(() => {
    if (pageModel?.['mboxData']?.length > 0 && Object.keys(dcaResponseByMbox).length === 0) {
      const tabMeta = tabItemList.find(t => t.tabTitle === selectedTab);
      const mboxes = tabMeta?.tabDecisionScopes ?? [];
      if (!mboxes.length) return;
      const mboxItems = (pageModel['mboxData'] as Record<string, unknown>[]).filter((item: Record<string, unknown>) => {
        const name = String(item?.['mboxName'] ?? '');
        return name && mboxes.includes(name);
      });
      const saveResponse = resolveMboxResponses(mboxItems, mboxes as string[]);
      if (Object.keys(saveResponse).length > 0) {
        setDCAResponseByMbox(saveResponse);
      }
    }
  }, [pageModel, tabDetails, selectedTab, dcaResponseByMbox]);

  const getDcaData = useCallback(() => {
    const tabMeta = tabItemList.find(t => t.tabTitle === selectedTab);
    const tabMboxesList = tabMeta?.tabDecisionScopes ?? [];

    const missingMboxes = tabMboxesList.filter((mboxName): mboxName is string => {
      if (!mboxName) return false;
      const existing = dcaResponseByMbox[mboxName] as unknown as
        | {
            isTargetContentError?: boolean;
            loadCfData?: boolean;
            fromUxlPageModel?: boolean;
          }
        | null
        | undefined;

      // No response exists yet — needs fetching
      if (existing == null) return true;

      // Skip re-fetch if loadCfData is true (empty but resolved response)
      if (existing?.loadCfData === true) return false;

      // Only retry errors that didn't come from pageModel UXL
      if (existing?.isTargetContentError === true) {
        return existing?.fromUxlPageModel !== true;
      }

      return false;
    });

    // create a local copy of this component model
    // with arrays converted to objects so it can traverse into tabDetails/dcaTilesList and extract mboxes.
    const arraysToObjects = (value: unknown): unknown => {
      if (Array.isArray(value)) {
        return value.reduce<Record<string, unknown>>((acc, item, idx) => {
          acc[String(idx)] = arraysToObjects(item);
          return acc;
        }, {});
      }
      if (value && typeof value === 'object') {
        const obj = value as Record<string, unknown>;
        const next: Record<string, unknown> = {};
        Object.keys(obj).forEach(k => {
          next[k] = arraysToObjects(obj[k]);
        });
        return next;
      }
      return value;
    };

    const modelCopy = arraysToObjects(JSON.parse(JSON.stringify(model))) as unknown as IPageModel;
    const { mboxParameters } = getMboxParamters(modelCopy);
    const allow = new Set(missingMboxes);
    const seen = new Set<string>();

    const payloadMboxes: IReturnTypeOFGetMboxParam[] = [];
    for (const item of (mboxParameters as IReturnTypeOFGetMboxParam[]) || []) {
      const name = String((item as { mboxName?: string } | undefined)?.mboxName ?? '');
      if (allow.has(name) && !seen.has(name)) {
        payloadMboxes.push(item);
        seen.add(name);
      }
    }
    if (payloadMboxes.length > 0 && datalayer) {
      const alloyPromise = fetchDCADynamicModelForAlloy(payloadMboxes, {} as IPageModel, datalayer, mvpOffers);
      if (alloyPromise?.then) {
        void alloyPromise
          .then((response: unknown) => {
            const merged = mergeAlloyMboxResponses(payloadMboxes, response);
            setDCAResponseByMbox(prev => ({ ...prev, ...merged }));
          })
          .catch((err: unknown) => {
            console.warn('[getDcaData] Alloy DCA fetch failed:', err);
            const merged = mergeAlloyMboxResponses(payloadMboxes, null);
            setDCAResponseByMbox(prev => ({ ...prev, ...merged }));
          });
      }
    }
  }, [datalayer, mvpOffers, model, selectedTab, tabItemList, dcaResponseByMbox]);
  async function getCFDataFromApi(tabTitle: string): Promise<CfNode[]> {
    try {
      // Execute UXL query using utility function
      const response = await getUxlForDTL(tabTitle);
      if (!response) {
        return [];
      }

      // Parse response - handle both success and error cases
      const aemContentResponse = response?.content?.aemContentByQueryName as
        | { __typename?: string; content?: string | unknown }
        | undefined;

      // Handle ResponseError case
      if (aemContentResponse?.__typename === 'ResponseError') {
        console.error('[getCFDataFromApi] ResponseError:', aemContentResponse);
        return [];
      }

      // Extract content from success response
      // The content field is a JSON string that needs to be parsed
      const contentString = aemContentResponse?.content;
      if (!contentString) {
        console.warn('[getCFDataFromApi] No content in response');
        return [];
      }

      let contentData;
      try {
        // Parse the JSON string content
        contentData = typeof contentString === 'string' ? JSON.parse(contentString) : contentString;
      } catch (parseError) {
        console.error('[getCFDataFromApi] Error parsing content JSON:', parseError);
        return [];
      }

      // Extract cards array from the parsed content
      // Structure should be similar to AEM response: tabCollectionList.items[0].cards
      const nodes =
        contentData?.data?.tabCollectionList?.items?.[0]?.cards ??
        contentData?.tabCollectionList?.items?.[0]?.cards ??
        [];

      // Normalize using existing utility function
      const normalized = normalizeCfItems(nodes, tabTitle);
      return normalized;
    } catch (e) {
      console.error('[getCFDataFromApi] Error calling UXL endpoint:', e);
      return [];
    }
  }
  // Ref to hold latest tabItemList so fetchTabData can stay stable (avoids effect loop when
  // parent passes new tabDetails reference on every render, e.g. from AEM ResponsiveGrid).
  const tabItemListRef = useRef(tabItemList);
  tabItemListRef.current = tabItemList;

  // fetch: server provides CF-filled slots + decisionScopes + slotsMeta
  const inFlightRef = useRef<Set<string>>(new Set());
  // eslint-disable-next-line react-hooks/exhaustive-deps -- tabItemList excluded: read from tabItemListRef to avoid effect loop when parent passes new tabDetails ref each render
  const fetchTabData = useCallback(
    async (tabTitle: string) => {
      if (tabSlotsRef.current[tabTitle]) return; // already loaded (e.g., SSR-provided)
      if (inFlightRef.current.has(tabTitle)) return;
      inFlightRef.current.add(tabTitle);

      try {
        const tabItemList = tabItemListRef.current;
        const tabMeta = tabItemList.find(t => t.tabTitle === tabTitle);
        if (!tabMeta) return;

        const tabDecisionScopes = tabMeta.tabDecisionScopes ?? [];
        const contentFragments =
          isAuthorMode || isMockCFDataEnabled()
            ? await fetchCFData(tabTitle, isAuthorMode)
            : await getCFDataFromApi(tabTitle);
        const mergedSlots = mergeCfAndDcaToSlots(
          tabMeta.tabPatternSlots || [],
          tabMeta.tabDCAtiles || [],
          contentFragments
        );

        setTabSlots(prev => {
          const next = { ...prev, [tabTitle]: mergedSlots || [] };
          return next;
        });
        // Pass mergedSlots directly to avoid race: setState is async, so reading from state
        // in populateDcaFromAlloy may see prev[tabTitle] as undefined before the update commits.
        void populateDcaFromAlloy(tabTitle, tabMeta.tabDCAtiles, tabDecisionScopes as string[], mergedSlots || []);
      } catch (err) {
        console.error('Error fetching dynamic tab data', err);
      } finally {
        inFlightRef.current.delete(tabTitle);
      }
    },
    [tabItemList]
  );

  // DCA overlay (client-side). If NEXT_PUBLIC_USE_MOCK_DCADATA === 'true' use dcaMockData; else use store responses.
  const populateDcaFromAlloy = useCallback(
    async (tabTitle: string, dcaTiles?: DcaNode[], _tabDecisionScopes?: string[], slotsFromCaller?: SlotContent[]) => {
      const useMock = isMockDCADataEnabled();
      try {
        if (!dcaTiles?.length) return;

        const orderByMbox = new Map<string, number>();
        for (const t of dcaTiles) {
          const mboxParam = t?.mboxParameter;
          const ord = Number(t?.order);
          if (mboxParam && !Number.isNaN(ord)) orderByMbox.set(mboxParam, ord);
        }

        const responseByMbox = useMock ? (dcaMockData as Record<string, unknown>) : dcaResponseByMbox ?? {};
        const dcaNodes: DcaNode[] = dcaTiles
          .map(tile => {
            const mbox = tile?.mboxParameter;
            if (!mbox) return null;
            const payload = responseByMbox[mbox];
            if (!payload) return null;
            return {
              mboxParameter: mbox,
              order: orderByMbox.get(mbox),
              payload,
            } as DcaNode;
          })
          .filter((n): n is DcaNode => Boolean(n));

        if (!slotsFromCaller?.length) return;
        const updated = overlaySlotsWithDcaRes(slotsFromCaller, dcaNodes);
        setTabSlots(prev => {
          const prevSlots = prev[tabTitle];
          if (isSameTabSlotArray(prevSlots, updated as SlotContent[])) return prev;
          return { ...prev, [tabTitle]: updated as SlotContent[] };
        });
      } catch (e) {
        console.warn('[populateDcaFromAlloy] DCA overlay failed, using cfFallback for DCA slots', e);
        if (slotsFromCaller?.length) {
          const updated = overlaySlotsWithDcaRes(slotsFromCaller, [], { fillFromCFFallbackIfEmpty: true });
          setTabSlots(prev => {
            const prevSlots = prev[tabTitle];
            if (isSameTabSlotArray(prevSlots, updated as SlotContent[])) return prev;
            return { ...prev, [tabTitle]: updated as SlotContent[] };
          });
        }
      }
    },
    [dcaResponseByMbox]
  );

  useEffect(() => {
    // Mark hydration complete; data from SSR is already present in state
    setIsHydrated(true);
  }, []);

  // If SSR provided requestedTabData, attempt to overlay with available DCA responses after hydration
  // (restricted to the SSR tab only, not the client-selected tab).
  useEffect(() => {
    if (!isHydrated) return; // wait for hydration to complete
    if (!requestedTab || !requestedTabData) return; // no requested tab data
    const tabMeta = tabItemList.find(t => t.tabTitle === requestedTab);
    if (!tabMeta) return;
    const slots = Array.isArray(requestedTabData) ? requestedTabData : [];
    void populateDcaFromAlloy(
      requestedTab,
      tabMeta.tabDCAtiles,
      tabMeta.tabDecisionScopes as string[],
      slots as SlotContent[]
    );
  }, [isHydrated, requestedTab, requestedTabData]);

  // Fetch when selectedTab changes (client-side effect; SSR never runs effects).
  useEffect(() => {
    if (!selectedTab) return;
    void fetchTabData(selectedTab);
  }, [selectedTab, fetchTabData]);

  // On tab changes (not initial load), fetch missing DCA data for that tab.
  // Use a previous-tab guard (instead of "skip first run") to avoid React StrictMode double-effect
  // invoking `getDcaData` on initial hydration.
  const prevSelectedTabRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isHydrated) return;
    if (!selectedTab) return;

    const prev = prevSelectedTabRef.current;
    prevSelectedTabRef.current = selectedTab;

    // First hydrated run: just initialize prev
    if (prev == null) return;
    // No actual tab change (e.g. StrictMode re-run): do nothing
    if (prev === selectedTab) return;

    getDcaData();
  }, [isHydrated, selectedTab, getDcaData]);

  // When DCA responses arrive, overlay them onto already-loaded slots (avoid loops).
  useEffect(() => {
    if (!isHydrated) return;
    if (!selectedTab) return;
    const slots = tabSlots[selectedTab];
    if (!Array.isArray(slots) || slots.length === 0) return;
    const tabMeta = tabItemList.find(t => t.tabTitle === selectedTab);
    if (!tabMeta) return;
    const needsOverlay = slots.some(s => {
      if (s?.type !== 'dca') return false;
      const p = s?.payload;
      if (p !== null && p !== undefined && !isDcaTargetPayloadFailed(p)) return false;
      const mbox = s?.dcaTarget?.mboxParameter;
      return Boolean(mbox && dcaResponseByMbox?.[mbox]);
    });
    if (!needsOverlay) return;
    void populateDcaFromAlloy(
      selectedTab,
      tabMeta.tabDCAtiles,
      tabMeta.tabDecisionScopes as string[],
      slots as SlotContent[]
    );
  }, [isHydrated, selectedTab, tabSlots, tabItemList, dcaResponseByMbox, populateDcaFromAlloy]);

  // Sync URL with selectedTab on hydration — handles edge cases where the URL
  // still contains a stale/invalid tab value (e.g. browser restored from bfcache).
  useEffect(() => {
    if (!isHydrated) return;
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    // If URL tab doesn't match selected tab, silently correct the URL
    if (tabParam && tabParam !== selectedTab) {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', selectedTab);
      window.history.replaceState(null, '', url.toString());
    }
  }, [isHydrated]); // intentionally run once after hydration

  // sync query param (?tab=) changes via browser navigation -> selectedTab
  useEffect(() => {
    const onPopState = () => {
      const params = new URLSearchParams(window.location.search);
      const tabParam = params.get('tab');
      const firstTab = tabItemList[0]?.tabTitle;

      // No tab param — default to first tab and correct the URL
      if (!tabParam) {
        if (firstTab) {
          startTransition(() => setSelectedTab(firstTab));
          const url = new URL(window.location.href);
          url.searchParams.set('tab', firstTab);
          window.history.replaceState(null, '', url.toString());
        }
        return;
      }

      // Valid tab — select it (case-insensitive match)
      const matched = tabItemList.find(tab => tab.tabTitle.toLowerCase() === tabParam.toLowerCase());
      if (matched) {
        startTransition(() => setSelectedTab(matched.tabTitle));
      } else if (firstTab) {
        // Invalid tab — default to first tab and correct the URL
        startTransition(() => setSelectedTab(firstTab));
        const url = new URL(window.location.href);
        url.searchParams.set('tab', firstTab);
        window.history.replaceState(null, '', url.toString());
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [tabItemList]);

  // click handler: update selectedTab (fetchTabData runs via effect)
  const onTabSelect = useCallback((tabTitle: string) => {
    startTransition(() => setSelectedTab(tabTitle));
    const url = new URL(window.location.href);
    url.searchParams.set('tab', tabTitle);
    window.history.pushState(null, '', `${url.pathname}?${url.searchParams.toString()}`);
  }, []);

  // selectedTabMeta is the metadata for the selected tab which includes the tab title, dynamic media, and pattern slots
  const selectedTabMeta = useMemo(() => tabItemList.find(t => t.tabTitle === selectedTab), [tabItemList, selectedTab]);
  const selectedTabDynamicMedia = useMemo(
    () => selectedTabMeta?.dynamicMedia ?? (tabItemList[0]?.dynamicMedia as renditions),
    [selectedTabMeta, tabItemList]
  );
  const firedNonCobrandImpressionKeysRef = useRef<Set<string>>(new Set());
  const lastNonCobrandImpressionTabRef = useRef<string | undefined>(undefined);

  // currentSlots is the slots content for the selected tab
  const currentSlots = tabSlots[selectedTab] ?? [];
  // panelSlots is the slots content for the selected tab
  const panelSlots = useMemo(() => {
    const patternSlots = selectedTabMeta?.tabPatternSlots ?? [];
    if (!patternSlots.length) return currentSlots;
    if (!currentSlots.length) return patternSlots as SlotContent[];

    const slotsByPosition = new Map<number, SlotContent>();
    currentSlots.forEach(slot => {
      if (typeof slot?.position === 'number') slotsByPosition.set(slot.position, slot);
    });

    return patternSlots.map(pattern => {
      const match = slotsByPosition.get(pattern.position);
      if (!match) return pattern as SlotContent;
      // Pattern carries grid meta (size, position, authored type). Loaded `match` has overlay state
      // (type/payload after DCA or CF fallback) and must win so we do not reset promoted `cf` slots back to `dca`.
      return { ...pattern, ...match } as SlotContent;
    });
  }, [selectedTabMeta, currentSlots]);

  const slotItems = useMemo(() => {
    const rawTabIndex = tabItemList.findIndex(t => t.tabTitle === selectedTab);
    const tabIndex = rawTabIndex >= 0 ? rawTabIndex : 0;
    const tabName = tabItemList[tabIndex]?.tabTitle || '';
    const oneBasedTabIndex = tabIndex + 1;
    const tabTrackingPosition = buildTabTrackingPrefix(oneBasedTabIndex, tabName);
    let cardIndex = 0;

    return (panelSlots || []).map(slot => {
      // Include tab in the key so switching tabs remounts slot cards.
      const key = `tab-${oneBasedTabIndex}-slot-${slot?.position ?? 0}`;
      const size = (slot?.size as 'square' | 'tall' | 'banner') ?? 'square';
      const isBanner = size === 'banner';
      const currentCardIndex = isBanner ? 0 : cardIndex++;
      const colClass = isBanner ? '' : `pos-${(currentCardIndex % 3) + 1}`;
      return (
        <StyledTabPanelItem key={key} className={clsx(size, colClass, slot?.type === 'dca' ? 'dca-slot' : 'cf-slot')}>
          {resolveSlotComponent(slot, {
            index: slot?.position ?? 0,
            tabIndex: oneBasedTabIndex,
            tabName,
            slotSize: size,
            trackingProperties: {
              ...trackingProperties,
              tabTrackingContentPosition: tabTrackingPosition,
            },
          })}
        </StyledTabPanelItem>
      );
    });
  }, [panelSlots, tabItemList, selectedTab, trackingProperties]);

  const nonCobrandDcaImpressionEntries = useMemo(() => {
    const rawTabIndex = tabItemList.findIndex(t => t.tabTitle === selectedTab);
    const tabIndex = rawTabIndex >= 0 ? rawTabIndex : 0;
    const tabName = tabItemList[tabIndex]?.tabTitle || '';
    const oneBasedTabIndex = tabIndex + 1;
    const tabTrackingPosition = buildTabTrackingPrefix(oneBasedTabIndex, tabName);
    const entries = (panelSlots || [])
      .map(slot =>
        getDcaNonCobrandImpressionEntry(slot, {
          index: slot?.position ?? 0,
          tabIndex: oneBasedTabIndex,
          tabName,
          slotSize: (slot?.size as 'square' | 'tall' | 'banner') ?? 'square',
          trackingProperties: {
            ...trackingProperties,
            tabTrackingContentPosition: tabTrackingPosition,
          },
        })
      )
      .filter((entry): entry is string => Boolean(entry));

    return Array.from(new Set(entries));
  }, [panelSlots, tabItemList, selectedTab, trackingProperties]);

  useEffect(() => {
    if (!nonCobrandDcaImpressionEntries.length) return;
    if (typeof window === 'undefined') return;
    const impressionTrack = (window as unknown as { impressionTrack?: (value: string) => void }).impressionTrack;
    if (typeof impressionTrack !== 'function') return;

    // Repeat visit: leaving a tab clears dedupe so returning to the same tab fires the batch again.
    if (lastNonCobrandImpressionTabRef.current !== selectedTab) {
      firedNonCobrandImpressionKeysRef.current.clear();
      lastNonCobrandImpressionTabRef.current = selectedTab;
    }

    const impressionValue = `event168|list3=${nonCobrandDcaImpressionEntries.join('^')}`;
    const impressionKey = `${selectedTab}|${impressionValue}`;
    if (firedNonCobrandImpressionKeysRef.current.has(impressionKey)) return;

    impressionTrack(impressionValue);
    firedNonCobrandImpressionKeysRef.current.add(impressionKey);
  }, [nonCobrandDcaImpressionEntries, selectedTab]);

  return (
    <StyledDynamicTabbedLayout
      className={clsx('m-container-fullbleed', styleclass)}
      data-target-component={targetComponentName || null}
      data-testid="homepage-DynamicTabbedLayout"
      data-component-name="o-homepage-DynamicTabbedLayout"
    >
      <TabBanner theme={theme} dynamicMedia={selectedTabDynamicMedia} />

      <TabNavigation
        selectedTab={selectedTab}
        setSelectedTab={onTabSelect}
        tabNavigationList={tabItemList}
        pageTitle={pageTitle}
        isNextGenSearchForm={isNextGenSearchForm}
        isConvoAIActive={isConvoAIActive}
      >
        <div id={`${selectedTab}-panel`} role="tabpanel" aria-labelledby={selectedTabMeta?.tabValue || ''}>
          <StyledTabPanelGrid>{slotItems}</StyledTabPanelGrid>
        </div>
      </TabNavigation>
    </StyledDynamicTabbedLayout>
  );
};
